import {filter, find, get, includes, map, isArray} from "lodash";
import {EntityTypes} from "../../Notifications/NotificationsLib";
import {getSignDocPrefix} from "../../SignDoc/SignDocLib";

/**
 * Gets SignDoc options for filtering based on selected organizations
 *
 * @param {object} params - Function parameters
 * @param {Array<string>} params.selectedOrgOptions - Selected organization IDs or ['all']
 * @param {Array<object>} params.adminOrganizations - Full organization data with signDocs
 * @param {Array<string>} params.categories - Array of SignDoc categories to filter (e.g., ['CERTIFICATION', 'FORCE_COMPLETION', 'KNOWLEDGE_CENTER'])
 * @param {object} params.i18n - Translation context
 * @param {Array<string>} [params.types=null] - Optional array of SignDoc types to filter
 * @returns {object} Object containing signDocOptionsTmp array
 */
export const getSignDocOptions = ({
                                      selectedOrgOptions = [],
                                      adminOrganizations,
                                      categories = [], // Array of SignDoc categories to filter (e.g., ['CERTIFICATION', 'FORCE_COMPLETION', 'KNOWLEDGE_CENTER'])
                                      i18n,
                                      types = null // Optional array of SignDoc types to filter (e.g., ['FRONTAL_COURSE', 'ONLINE_COURSE'])
                                  }) => {
    let signDocOptionsTmp = []
    // Filter out any undefined/null values and ensure we have a valid array
    const validSelectedOrgOptions = isArray(selectedOrgOptions)
        ? filter(selectedOrgOptions, Boolean)
        : [];

    if (includes(validSelectedOrgOptions, 'all')) {
        //get all courses from all organizations
        let orgSignDocs = []
        map(adminOrganizations, (org) => {
            let tmpOrgSignDocs = get(org, 'organization.signDocs')
            tmpOrgSignDocs = filter(tmpOrgSignDocs, (signDoc) => {
                // Filter by type if types parameter is provided
                if (types && Array.isArray(types) && types.length > 0) {
                    if (!includes(types, signDoc.type)) {
                        return false;
                    }
                }

                // Filter by category if categories parameter is provided
                if (isArray(categories) && categories.length > 0) {
                    if (!includes(categories, signDoc.category)) {
                        return false;
                    }
                }

                const orgSignDoc = find(signDoc.organizationSignDoc, {OrganizationId: get(org, 'organization.id')})

                return get(orgSignDoc, 'isValid');
            })
            orgSignDocs = orgSignDocs.concat(tmpOrgSignDocs)
        })

        signDocOptionsTmp = map(orgSignDocs, (signDoc) => {
            let prefix = getSignDocPrefix({signDoc, i18n});
            return {
                value: signDoc.id,
                text: `${prefix} - ${signDoc.docName}`,
                type: EntityTypes.SIGN_DOC
            }
        })


    } else {
        // Handle multiple specific organizations
        let allOrgSignDocs = {};
        // Collect all signDocs from selected organizations
        map(validSelectedOrgOptions, (orgId) => {
            const orgData = find(adminOrganizations, {organization: {id: orgId}});
            if (orgData) {
                const orgDocs = get(orgData, 'organization.signDocs', []);
                map(orgDocs, (signDoc) => {
                    // Filter by type if types parameter is provided
                    if (types && Array.isArray(types) && types.length > 0) {
                        if (!types.includes(signDoc.type)) {
                            return; // Skip this signDoc
                        }
                    }

                    // Filter by category if categories parameter is provided
                    if (categories && Array.isArray(categories) && categories.length > 0) {
                        if (!categories.includes(signDoc.category)) {
                            return; // Skip this signDoc
                        }
                    }

                    const orgSignDoc = find(signDoc.organizationSignDoc, {OrganizationId: orgId})
                    // Only check isValid - categories already filtered above
                    if (orgSignDoc?.isValid) {
                        allOrgSignDocs[signDoc.id] = signDoc;
                    }
                })
            }
        });

        signDocOptionsTmp = map(Object.values(allOrgSignDocs), (signDoc) => {
            let prefix = getSignDocPrefix({signDoc, i18n});
            return {
                value: signDoc.id,
                text: `${prefix} - ${signDoc.docName}`,
                type: EntityTypes.SIGN_DOC
            }
        })
    }
    //unique signDocs options
    signDocOptionsTmp = filter(signDocOptionsTmp, (signDoc, index, self) => index === self.findIndex((t) => (t.value === signDoc.value)))
    signDocOptionsTmp.unshift({
        value: 'all', text: i18n.translate('allSignDocs')
    })
    //add empty option to signDocs
    return {signDocOptionsTmp};
}

export const DashboardChartLabels = ({i18n, hasExamFeatureFlag = false}) => {
    const labels = {
        completed: {
            colorClass: 'colorYellow',
            label:
                i18n.translate('ReportsPage.courseLegend.completed')
        },
        inProgress: {
            colorClass: 'colorGrey',
            label:
                i18n.translate('ReportsPage.courseLegend.inProgress')
        },
        notStarted: {
            colorClass: 'colorLightGrey',
            label:
                i18n.translate('ReportsPage.courseLegend.notStarted')
        }
    };
    
    // Only include the failed label if the company has the exam feature flag enabled
    if (hasExamFeatureFlag) {
        labels.failed = {
            colorClass: 'colorRed',
            label: i18n.translate('ReportsPage.courseLegend.failed')
        };
    }
    
    return labels;
}